import ApiClient from '@config/network';
import { FormValues, STEP } from '@types';
import { isServerError } from '@types.guard';
import { convertStepToRoute } from '@utils/convertRoutes';
import parseServerErrors from '@utils/parseServerErrors';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCheckoutConfigContext } from './CheckoutConfig';
import * as Sentry from '@sentry/react';
import composeErrorMessage from '@utils/composeErrorMessage';

export interface StepperState {
  lastStep: STEP;
  currentStep: STEP;

  goToStep: (step: STEP, skipValidation?: boolean) => Promise<void>;
  isValidating: boolean;
  serverError: null | string;
}

const StepperContext = createContext<StepperState>({} as StepperState);
export const useStepper = () => useContext(StepperContext);

const ORDERED_STEPS = [STEP.DONATION, STEP.SUPPORTER, STEP.PAYMENT];

export const StepperProvider = ({
  children,
  initStep,
}: {
  children: Array<JSX.Element> | JSX.Element;
  initStep?: STEP;
}): JSX.Element => {
  const { trigger, getValues, setError } = useFormContext<FormValues>();
  const { t } = useTranslation();
  const { checkout } = useCheckoutConfigContext();
  const navigate = useNavigate();
  const location = useLocation();

  const stepNavigate = useCallback(
    (step: STEP) => {
      setCurrentStep(step);
      navigate(convertStepToRoute(step) + location.search);
    },
    [navigate],
  );

  const [currentStep, setCurrentStep] = useState<STEP>(initStep || STEP.DONATION);
  const [lastStep, setLastStep] = useState<STEP>(currentStep);
  const [isValidating, setIsValidating] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (ORDERED_STEPS.indexOf(currentStep) > ORDERED_STEPS.indexOf(lastStep)) {
      setLastStep(currentStep);
    }
  }, [currentStep]);

  const goToStep = useCallback(
    async (step: STEP, skipValidation = false) => {
      if (skipValidation) {
        stepNavigate(step);
        return;
      }

      setIsValidating(true);
      setServerError(null);

      // ⚠️ Case is the EXIT page where the goToStep is called
      switch (currentStep) {
        case STEP.DONATION:
          {
            // Check client side errors
            const isValid = await trigger(['amount', 'frequency']);

            if (isValid) {
              try {
                // Check server side errors
                await ApiClient.patch(`/rest/v1/checkout/${checkout.token}`, {
                  donation: {
                    amount: getValues('amount'),
                    frequency: getValues('frequency'),
                  },
                });

                // Everything is ok, proceed to next step
                stepNavigate(step);
              } catch (error: any) {
                const serverErrors = parseServerErrors(error);

                if (!serverErrors || serverErrors?.some((se) => !isServerError(se))) {
                  console.error(error);
                  setServerError(t('Something went wrong! Try again'));
                  Sentry.captureMessage(
                    composeErrorMessage({
                      code: 'SERVER_ERROR',
                      message: error?.message,
                      errorObject: error,
                      checkout,
                    }),
                  );

                  setIsValidating(false);
                  return;
                }

                for (const serverError of serverErrors) {
                  const [entity, field, errorType] = serverError.code.split('|');

                  if (entity === 'supporter') {
                    const errorMsg =
                      errorType === 'REQUIRED'
                        ? { type: 'required', message: t('This field is required') }
                        : { type: 'invalid', message: t("This field it's not valid") };

                    switch (field) {
                      case 'AMOUNT':
                        setError('amount', errorMsg);
                        break;
                      case 'FREQUENCY':
                        setError('frequency', errorMsg);
                        break;
                      default:
                        setServerError(t('Something went wrong! Try again'));
                        Sentry.captureMessage(
                          composeErrorMessage({
                            code: 'UNKNOWN_VALIDATION_ERROR',
                            message: error?.message,
                            errorObject: error,
                            checkout,
                          }),
                        );
                        break;
                    }
                  } else {
                    setServerError(t('Something went wrong! Try again'));
                    Sentry.captureMessage(
                      composeErrorMessage({
                        code: 'UNKNOWN_VALIDATION_DOMAIN',
                        message: error?.message,
                        errorObject: error,
                        checkout,
                      }),
                    );
                  }
                }
              }
            }
          }
          break;

        case STEP.SUPPORTER:
          {
            // Check client side errors
            const isValid = await trigger([
              'legalPerson',
              'firstName',
              'lastName',
              'businessName',
              'ssn',
              'vat',
              'email',
              'phone',
              'mobile',
              'country',
              'city',
              'locality',
              'postalCode',
              'address',
              // 'privacyAccepted',
              // "acceptedPrivacyFields"
            ]);

            if (isValid) {
              const privacy_values = {};

              checkout.campaign.privacy_fields.forEach((field) => {
                privacy_values[field.code] = {
                  value: !!getValues('acceptedPrivacyFields').includes(field.code),
                };
              });

              // Check server side errors
              try {
                await ApiClient.patch(`/rest/v1/checkout/${checkout.token}`, {
                  supporter: {
                    type: getValues('legalPerson'),
                    first_name: getValues('firstName'),
                    last_name: getValues('lastName'),
                    business_name: getValues('businessName'),
                    ssn: getValues('ssn'),
                    vat: getValues('vat'),
                    email: getValues('email'),
                    phone: getValues('phone'),
                    mobile: getValues('mobile'),
                    country: getValues('country'),
                    city: getValues('city'),
                    locality: getValues('locality'),
                    postal_code: getValues('postalCode'),
                    address: getValues('address'),
                    privacy_fields: getValues('acceptedPrivacyFields'),
                    privacy_values,
                  },
                });

                // Everything is ok, proceed to next step
                stepNavigate(step);
              } catch (error: any) {
                const serverErrors = parseServerErrors(error);

                if (!serverErrors || serverErrors?.some((se) => !isServerError(se))) {
                  console.error(error);
                  setServerError(t('Something went wrong! Try again'));
                  Sentry.captureMessage(
                    composeErrorMessage({
                      code: 'SERVER_ERROR',
                      message: error?.message,
                      errorObject: error,
                      checkout,
                    }),
                  );
                  setIsValidating(false);
                  return;
                }

                for (const serverError of serverErrors) {
                  const [entity, field, errorType] = serverError.code.split('|');

                  if (entity === 'supporter') {
                    const errorMsg =
                      errorType === 'REQUIRED'
                        ? { type: 'required', message: t('This field is required') }
                        : { type: 'invalid', message: t("This field it's not valid") };

                    switch (field) {
                      case 'TYPE':
                        setError('legalPerson', errorMsg);
                        break;
                      case 'FIRST_NAME':
                        setError('firstName', errorMsg);
                        break;
                      case 'LAST_NAME':
                        setError('lastName', errorMsg);
                        break;
                      case 'BUSINESS_NAME':
                        setError('businessName', errorMsg);
                        break;
                      case 'SSN':
                        setError('ssn', errorMsg);
                        break;
                      case 'VAT':
                        setError('vat', errorMsg);
                        break;
                      case 'EMAIL':
                        setError('email', errorMsg);
                        break;
                      case 'PHONE':
                        setError('phone', errorMsg);
                        break;
                      case 'MOBILE':
                        setError('mobile', errorMsg);
                        break;
                      case 'COUNTRY':
                        setError('country', errorMsg);
                        break;
                      case 'CITY':
                        setError('city', errorMsg);
                        break;
                      case 'LOCALITY':
                        setError('locality', errorMsg);
                        break;
                      case 'POSTAL_CODE':
                        setError('postalCode', errorMsg);
                        break;
                      case 'ADDRESS':
                        setError('address', errorMsg);
                        break;
                      default:
                        // known issue - show only last error each validation
                        setServerError(t('Something went wrong! Try again'));
                        Sentry.captureMessage(
                          composeErrorMessage({
                            code: 'UNKNOWN_VALIDATION_ERROR',
                            message: error?.message,
                            errorObject: error,
                            checkout,
                          }),
                        );
                        break;
                    }
                  } else {
                    setServerError(t('Something went wrong! Try again'));
                    Sentry.captureMessage(
                      composeErrorMessage({
                        code: 'UNKNOWN_VALIDATION_DOMAIN',
                        message: error?.message,
                        errorObject: error,
                        checkout,
                      }),
                    );
                  }
                }
              }
            }
          }
          break;

        case STEP.PAYMENT:
          stepNavigate(step);
          break;

        case STEP.THANK_YOU:
          stepNavigate(step);
          break;
      }
      setIsValidating(false);
    },
    [currentStep, checkout.token],
  );

  return (
    <StepperContext.Provider
      value={{
        lastStep,
        currentStep,
        goToStep,
        isValidating,
        serverError,
      }}
    >
      {children}
    </StepperContext.Provider>
  );
};

export default StepperContext;
